import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Building2, GitBranch, Users, Key, Webhook, ArrowLeft, Edit, Package, FileText } from 'lucide-react';
import apiClient from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { IntegrationApiBaseBanner } from '../../components/IntegrationApiBaseBanner';

type Tab = 'overview' | 'branches' | 'users' | 'api-keys' | 'webhooks' | 'shipments' | 'notes';

export const SuperAdminCompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [company, setCompany] = useState<any>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [shipments, setShipments] = useState<any[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({ internalNotes: '', billingNotes: '' });
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      apiClient.getCompany(id),
      apiClient.listBranches(id, 100, 0),
      apiClient.listUsers(id, 100, 0),
      apiClient.listApiKeys(id),
      apiClient.listWebhookEvents(id, 20, 0),
      apiClient.listShipments({ companyId: id }, 20, 0),
    ]).then(([c, b, u, k, w, sh]) => {
      const comp = c.data.data; setCompany(comp);
      setNotes({ internalNotes: comp?.internalNotes || '', billingNotes: comp?.billingNotes || '' });
      const bd = b.data.data; setBranches(bd?.branches ?? bd?.rows ?? (Array.isArray(bd) ? bd : []));
      const ud = u.data.data; setUsers(ud?.users ?? ud?.rows ?? (Array.isArray(ud) ? ud : []));
      setApiKeys(k.data.data?.apiKeys ?? k.data.data ?? []);
      const wd = w.data.data; setWebhooks(wd?.events ?? wd?.rows ?? (Array.isArray(wd) ? wd : []));
      const sd = sh.data.data; setShipments(sd?.shipments ?? sd?.rows ?? (Array.isArray(sd) ? sd : []));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const revokeKey = async (keyId: string) => {
    await apiClient.revokeApiKey(id!, keyId);
    setApiKeys(prev => prev.filter(k => k.id !== keyId));
  };

  const toggleBranchStatus = async (b: any) => {
    const next = b.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    await apiClient.updateBranchStatus(id!, b.id, next);
    setBranches(prev => prev.map(x => x.id === b.id ? { ...x, status: next } : x));
  };

  const toggleUserStatus = async (u: any) => {
    const next = u.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    await apiClient.updateUser(u.id, { status: next });
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: next } : x));
  };

  const [branchModal, setBranchModal] = useState<any>(null); // null=closed, {}=new, {id,...}=edit
  const [branchForm, setBranchForm] = useState({ name: '', code: '', email: '', phone: '', address: '', city: '', status: 'ACTIVE' });
  const [savingBranch, setSavingBranch] = useState(false);

  const openBranchModal = (b?: any) => {
    setBranchForm(b ? { name: b.name, code: b.code, email: b.email ?? '', phone: b.phone ?? '', address: b.address ?? '', city: b.city ?? '', status: b.status } : { name: '', code: '', email: '', phone: '', address: '', city: '', status: 'ACTIVE' });
    setBranchModal(b ?? {});
  };

  const saveBranch = async () => {
    setSavingBranch(true);
    try {
      if (branchModal?.id) {
        await apiClient.updateBranch(id!, branchModal.id, branchForm);
      } else {
        await apiClient.createBranch(id!, branchForm);
      }
      setBranchModal(null);
      const b = await apiClient.listBranches(id!, 100, 0);
      const bd = b.data.data; setBranches(bd?.branches ?? bd?.rows ?? (Array.isArray(bd) ? bd : []));
    } catch {} finally { setSavingBranch(false); }
  };

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <Building2 size={15} /> },
    { key: 'branches', label: `Branches (${branches.length})`, icon: <GitBranch size={15} /> },
    { key: 'users', label: `Users (${users.length})`, icon: <Users size={15} /> },
    { key: 'shipments', label: `Shipments (${shipments.length})`, icon: <Package size={15} /> },
    { key: 'api-keys', label: `API Keys (${apiKeys.length})`, icon: <Key size={15} /> },
    { key: 'webhooks', label: `Webhooks (${webhooks.length})`, icon: <Webhook size={15} /> },
    { key: 'notes', label: 'Notes', icon: <FileText size={15} /> },
  ];

  if (loading) return <div className="loading-spinner">Loading company...</div>;
  if (!company) return <div className="empty-state"><h3>Company not found</h3></div>;

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate('/super-admin/companies')}><ArrowLeft size={16} /></button>
          <h1 className="page-title">{company.name}</h1>
          <span className={`badge badge-${company.status?.toLowerCase()}`}>{company.status}</span>
        </div>
        <Link to={`/super-admin/companies/${id}/edit`} className="btn btn-ghost"><Edit size={15} /> Edit</Link>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="btn btn-ghost"
            style={{ borderRadius: '6px 6px 0 0', borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent', color: tab === t.key ? '#3b82f6' : '#64748b', gap: 6 }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              ['Registration #', company.registrationNumber],
              ['Email', company.email],
              ['Phone', company.phone],
              ['Address', company.address],
              ['City', company.city],
              ['Country', company.country],
              ['Currency', company.currency],
              ['API Integration', company.allowApiIntegration ? 'Enabled' : 'Disabled'],
              ['Webhooks', company.allowWebhooks ? 'Enabled' : 'Disabled'],
            ].map(([k, v]) => (
              <div key={k as string}>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 2 }}>{k}</div>
                <div style={{ fontWeight: 500 }}>{v || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'branches' && (
        <div className="card">
          <div className="page-header" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>Branches</h2>
            <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => openBranchModal()}>+ New Branch</button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Name</th><th>Code</th><th>City</th><th>Phone</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {branches.map((b: any) => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 500 }}>{b.name}</td>
                    <td><code style={{ fontSize: 12 }}>{b.code}</code></td>
                    <td>{b.city || '—'}</td>
                    <td>{b.phone || '—'}</td>
                    <td><span className={`badge badge-${b.status?.toLowerCase()}`}>{b.status}</span></td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => openBranchModal(b)}>Edit</button>
                      <button className={`btn ${b.status === 'ACTIVE' ? 'btn-danger' : 'btn-success'}`} style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => toggleBranchStatus(b)}>
                        {b.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
                {branches.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>No branches</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'users' && (
        <div className="card">
          <div className="page-header" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>Users</h2>
            <Link to={`/super-admin/users/new?companyId=${id}`} className="btn btn-primary" style={{ fontSize: 13 }}>+ Add User</Link>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Branch</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {users.map((u: any) => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.firstName} {u.lastName}</td>
                    <td>{u.email}</td>
                    <td><span className="badge badge-inactive" style={{ fontSize: 10 }}>{u.role.replace(/_/g, ' ')}</span></td>
                    <td style={{ fontSize: 13 }}>{u.branch?.name ?? '—'}</td>
                    <td><span className={`badge badge-${u.status?.toLowerCase()}`}>{u.status}</span></td>
                    <td>
                      <button className={`btn ${u.status === 'ACTIVE' ? 'btn-danger' : 'btn-success'}`} style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => toggleUserStatus(u)}>
                        {u.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>No users</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'api-keys' && (
        <div className="card">
          <IntegrationApiBaseBanner />
          <div className="page-header" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>API Keys</h2>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Name</th><th>Key (masked)</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {apiKeys.map((k: any) => (
                  <tr key={k.id}>
                    <td>{k.name || '—'}</td>
                    <td><code style={{ fontSize: 12 }}>{k.keyPreview ?? '••••••••'}</code></td>
                    <td>{new Date(k.createdAt).toLocaleDateString()}</td>
                    <td><button className="btn btn-danger" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => revokeKey(k.id)}>Revoke</button></td>
                  </tr>
                ))}
                {apiKeys.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No API keys</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'webhooks' && (
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Webhook Events</h2>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Event</th><th>Status</th><th>Attempts</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {webhooks.map((w: any) => (
                  <tr key={w.id}>
                    <td>{w.eventType}</td>
                    <td><span className={`badge badge-${w.deliveryStatus?.toLowerCase()}`}>{w.deliveryStatus}</span></td>
                    <td>{w.deliveryAttempts ?? 0}</td>
                    <td>{new Date(w.createdAt).toLocaleDateString()}</td>
                    <td>
                      {w.deliveryStatus === 'FAILED' && (
                        <button className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => apiClient.retryWebhookEvent(w.id)}>Retry</button>
                      )}
                    </td>
                  </tr>
                ))}
                {webhooks.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>No webhook events</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'shipments' && (
        <div className="card">
          <div className="page-header" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>Recent Shipments</h2>
            <Link to={`/super-admin/shipments?companyId=${id}`} className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Tracking #</th><th>Sender</th><th>Receiver</th><th>Status</th><th>Amount</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {shipments.map((s: any) => (
                  <tr key={s.id}>
                    <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                    <td style={{ fontSize: 13 }}>{s.senderName}</td>
                    <td style={{ fontSize: 13 }}>{s.receiverName}</td>
                    <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{Number(s.totalAmount).toLocaleString()} {s.currency}</td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                    <td><Link to={`/super-admin/shipments/${s.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link></td>
                  </tr>
                ))}
                {shipments.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>No shipments</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'notes' && (
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Internal Notes (Super Admin Only)</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Internal Notes</label>
              <textarea value={notes.internalNotes} onChange={e => setNotes(n => ({ ...n, internalNotes: e.target.value }))}
                rows={4} style={{ width: '100%', resize: 'vertical' }}
                placeholder="Internal notes about this company (not visible to company users)..." />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Billing Notes</label>
              <textarea value={notes.billingNotes} onChange={e => setNotes(n => ({ ...n, billingNotes: e.target.value }))}
                rows={3} style={{ width: '100%', resize: 'vertical' }}
                placeholder="Billing-related notes, payment terms, special agreements..." />
            </div>
            <div>
              <button className="btn btn-primary" disabled={savingNotes} onClick={async () => {
                setSavingNotes(true);
                try {
                  await apiClient.updateCompanyNotes(id!, notes);
                  toast('Notes saved', 'success');
                } catch { toast('Failed to save notes', 'error'); }
                finally { setSavingNotes(false); }
              }}>
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branch create/edit modal */}
      {branchModal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 480, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>{branchModal?.id ? 'Edit Branch' : 'New Branch'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { label: 'Branch Name *', key: 'name' },
                { label: 'Code *', key: 'code' },
                { label: 'Email', key: 'email' },
                { label: 'Phone', key: 'phone' },
                { label: 'Address', key: 'address' },
                { label: 'City', key: 'city' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>{f.label}</label>
                  <input value={(branchForm as any)[f.key]} onChange={e => setBranchForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ width: '100%' }} />
                </div>
              ))}
              {branchModal?.id && (
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Status</label>
                  <select value={branchForm.status} onChange={e => setBranchForm(p => ({ ...p, status: e.target.value }))} style={{ width: '100%' }}>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setBranchModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveBranch} disabled={savingBranch || !branchForm.name || !branchForm.code}>
                {savingBranch ? 'Saving...' : 'Save Branch'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
